// Reusable scheduling utility to show/hide elements by date/time window.
// Timezone: Europe/London. All comparisons are done in London local time (handles GMT/BST automatically).
// Usage: add data-schedule-start and/or data-schedule-end attributes to any element.
// If either attribute is present, the element will be hidden by default in Liquid, then revealed by JS when now is in range.
// Also updates slideshow pagination when slides are shown/hidden.

/**
 * Normalize a schedule datetime string into a comparable ISO-like string in London time.
 * Accepts:
 *  - YYYY-MM-DD
 *  - YYYY-MM-DD HH:mm
 *  - YYYY-MM-DDTHH:mm
 * Missing time will default to 00:00 for start and 23:59 for end (controlled by isEnd flag at call site).
 * Returns undefined if input is blank/invalid.
 * Note: We treat provided values as London wall time strings, not absolute instants.
 * @param {string|undefined|null} str
 * @param {boolean} isEnd When true, date-only values default to end-of-day
 * @returns {string|undefined} e.g. '2025-08-26T09:30'
 */
function normalizeLondonLocal(str, isEnd) {
  if (!str) return undefined;
  const s = String(str).trim();
  if (!s) return undefined;
  // Normalize: drop timezone suffixes (Z or ±HH:mm) and convert space to 'T'
  const stripped = s
    .replace(' ', 'T')
    .replace(/Z$/i, '')
    .replace(/[+-]\d{2}:?\d{2}$/i, '');
  // Expect 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm[:ss]'
  const parts = stripped.split('T');
  const date = parts[0] ?? '';
  let time = parts[1] ?? (isEnd ? '23:59' : '00:00');
  // Trim seconds if provided HH:mm:ss -> HH:mm
  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) time = time.slice(0, 5);
  // Basic validation
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  if (!/^\d{2}:\d{2}$/.test(time)) return undefined;
  return `${date}T${time}`;
}

/**
 * Get current London time as an ISO-like string 'YYYY-MM-DDTHH:mm'.
 * Uses Intl API to account for DST correctly.
 * @returns {string}
 */
function nowLondonIso() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * Determine visibility of an element based on its data-schedule attributes in London time.
 * @param {HTMLElement} el
 * @param {string} londonNow 'YYYY-MM-DDTHH:mm'
 * @returns {'no-schedule'|'visible'|'hidden'}
 */
function checkVisibility(el, londonNow) {
  const startRaw = el.getAttribute('data-schedule-start') ?? '';
  const endRaw = el.getAttribute('data-schedule-end') ?? '';
  const start = normalizeLondonLocal(startRaw, false);
  const end = normalizeLondonLocal(endRaw, true);

  if (!start && !end) return 'no-schedule';

  // Invalid ranges are treated as hidden
  if (start && end && start > end) return 'hidden';

  if (start && londonNow < start) return 'hidden';
  if (end && londonNow > end) return 'hidden';

  return 'visible';
}

/**
 * Ensure a slideshow slide element has a stable slide-id for mapping.
 * Tries existing [slide-id], then [data-block-id]; falls back to auto id.
 * @param {HTMLElement} slide
 */
function ensureSlideId(slide) {
  if (slide.getAttribute('slide-id')) return;
  const fallback = slide.getAttribute('data-block-id');
  slide.setAttribute('slide-id', fallback || `slide-${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

/**
 * Rebuild dots or counter in a slideshow to reflect currently visible slides.
 * Assumes slides hidden via [hidden] should be excluded.
 * @param {HTMLElement} slideshowEl The <slideshow-component> instance
 */
function updateSlideshowPagination(slideshowEl) {
  // Only operate if the element exposes the expected API
  const instance = slideshowEl;
  const sc = /** @type {any} */ (instance);
  const slides = Array.from(slideshowEl.querySelectorAll('slideshow-slide')).filter((s) => !s.hasAttribute('hidden'));
  slides.forEach(ensureSlideId);

  // Ensure a valid selected slide: keep current if still visible, else select first visible
  if (slides.length) {
    const currentVisible = slideshowEl.querySelector('slideshow-slide[aria-hidden="false"]');
    const currentId = currentVisible?.getAttribute('slide-id') || '';
    const desiredIndex = currentId ? slides.findIndex((s) => s.getAttribute('slide-id') === currentId) : 0;
    const safeIndex = desiredIndex >= 0 ? desiredIndex : 0;
    const desiredSlideId = slides[safeIndex]?.getAttribute('slide-id');
    if (desiredSlideId) {
      // Use select by id so indices remain stable relative to visible slides
      sc.select?.({ id: desiredSlideId }, undefined, { animate: false });
  // Also update the attribute to keep component state consistent and trigger internal logic if needed
  slideshowEl.setAttribute('initial-slide', String(safeIndex));
    }
  }

  // Dots
  const controls = slideshowEl.querySelector('slideshow-controls');
  if (controls) {
    const dotsList = controls.querySelector('ol.slideshow-controls__dots');
    if (dotsList) {
      // Recreate dots to match visible slides and map by slide-id
      dotsList.innerHTML = '';
      slides.forEach((slide, index) => {
        const id = slide.getAttribute('slide-id');
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.className = 'slideshow-control button button-unstyled';
        btn.setAttribute('style', `animation-timeline: --slide-${index + 1}`);
        btn.setAttribute('aria-label', `Slide ${index + 1}`);
  // Use Component event system to select by id so indices stay correct
        // Attach a live click handler that calls the slideshow API directly so
        // dynamically generated dots behave like server-rendered controls.
        try {
          btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            try { sc.select?.({ id }, ev, { animate: true }); } catch (_) {}
          });
        } catch (_) {}
        btn.setAttribute('ref', 'dots[]');
        btn.textContent = String(index + 1);
  if (index === (sc.current ?? 0)) btn.setAttribute('aria-selected', 'true');
        li.appendChild(btn);
        dotsList.appendChild(li);
      });
    }

    // Counter
    const counter = controls.querySelector('.slideshow-controls__counter');
    if (counter) {
      const current = Math.min(sc.current ?? 0, Math.max(0, slides.length - 1));
      counter.innerHTML = `<span ref="current">${current + 1}</span><span class="slash">/</span>${slides.length}`;
    }
  }
}

/**
 * Apply schedule to a NodeList of elements. Toggle [hidden] and update related UI.
 * @param {HTMLElement[]} elements
 */
function applySchedule(elements) {
  const londonNow = nowLondonIso();
  /** @type {Set<HTMLElement>} */
  const affectedSlideshows = new Set();
  /** @type {Set<HTMLElement>} */
  const affectedGwpContainers = new Set();

  elements.forEach((el) => {
    const state = checkVisibility(el, londonNow);
    if (state === 'no-schedule') return;

    const shouldBeHidden = state === 'hidden';
    const isHidden = el.hasAttribute('hidden');

    if (shouldBeHidden && !isHidden) {
      el.setAttribute('hidden', '');
    } else if (!shouldBeHidden && isHidden) {
      el.removeAttribute('hidden');
    }

    // If the scheduled element sits inside a slideshow-slide, mirror the hidden state
    // to that enclosing slide so the slideshow component and pagination reflect the
    // actual visibility of the slide content.
    try {
      const parentSlide = el.closest && el.closest('slideshow-slide');
      if (parentSlide instanceof HTMLElement) {
        const parentIsHidden = parentSlide.hasAttribute('hidden');
        if (shouldBeHidden && !parentIsHidden) parentSlide.setAttribute('hidden', '');
        else if (!shouldBeHidden && parentIsHidden) parentSlide.removeAttribute('hidden');
      }
    } catch (_) {}
    // If element lives inside a slideshow, mark for pagination update
    const slideshow = el.closest('slideshow-component');
    if (slideshow instanceof HTMLElement) affectedSlideshows.add(slideshow);
    // Track GWP container visibility: hide when all slides hidden
    const gwp = el.closest('.gwp-offers');
    if (gwp instanceof HTMLElement) affectedGwpContainers.add(gwp);
  });

  // Run updates on the next animation frame so the slideshow component can react
  // to DOM changes (attribute toggles) and update its internal state first.
  affectedSlideshows.forEach((slideshow) => {
    try {
      requestAnimationFrame(() => updateSlideshowPagination(slideshow));
      // Fallback: run again shortly after in case the component needs extra time
      setTimeout(() => updateSlideshowPagination(slideshow), 120);
    } catch (_) {
      try { updateSlideshowPagination(slideshow); } catch (_) {}
    }
  });

  // Hide/show GWP containers depending on whether they have any visible slides
  affectedGwpContainers.forEach((gwp) => {
    try {
      const slidesRoot = gwp.querySelector('slideshow-slides') || gwp;
      const anyVisible = !!Array.from(slidesRoot.querySelectorAll('slideshow-slide')).some((s) => !s.hasAttribute('hidden'));
      if (!anyVisible) gwp.setAttribute('hidden', ''); else gwp.removeAttribute('hidden');
    } catch (_) {}
  });
}

/**
 * Find scheduled elements across the document.
 * @returns {HTMLElement[]}
 */
function findScheduled() {
  return Array.from(
    document.querySelectorAll('[data-schedule-start],[data-schedule-end]')
  );
}

// Initial run on DOM ready
function init() {
  applySchedule(findScheduled());

  // Observe DOM changes to re-apply for newly added nodes
  const mo = new MutationObserver((mutations) => {
    /** @type {HTMLElement[]} */
    const toCheck = [];
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        if (!(n instanceof HTMLElement)) return;
        if (n.matches('[data-schedule-start],[data-schedule-end]')) toCheck.push(n);
        n
          .querySelectorAll?.('[data-schedule-start],[data-schedule-end]')
          .forEach((e) => toCheck.push(/** @type {HTMLElement} */ (e)));
      });
      if (m.type === 'attributes' && m.target instanceof HTMLElement) {
        toCheck.push(m.target);
      }
    }
  if (toCheck.length) applySchedule(toCheck);
  });
  mo.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-schedule-start', 'data-schedule-end'],
  });

  // Re-run when tab becomes visible again (in case the London window of time changed)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) applySchedule(findScheduled());
  });

  // Also re-evaluate periodically to catch boundary crossings while tab stays open
  setInterval(() => applySchedule(findScheduled()), 60 * 1000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
